/**
 * Service for managing user places (private places stored in localStorage + encrypted file)
 *
 * Structure:
 * - localUuid: unique identifier for localStorage (generated client-side) - like contacts
 * - placeUuid: reference to places table UUID (optional - null for private places)
 * - role: 'CONTACT' (private), 'PUBLIC' (linked to DB), 'PRIVATE' (private establishment)
 * - placeTypeCode: reference to places_types.code (business category)
 * - isOwner: true if user claims ownership of this establishment
 */

import { createPlace as createPublicPlace } from './placesService';

const API_BASE_URL = 'https://apicabaza.creago.be/rest/cabaza/places';
const STORAGE_KEY = 'cabaza_user_places';

/**
 * En-têtes d'un appel qui écrit.
 *
 * Le serveur vérifie désormais que le fichier/lien vise appartient bien à
 * l'appelant. Il lui faut donc une identité prouvée : le JWT signé au login,
 * et non le `peopleuuid`/`peopleId`/`userId` que le corps de la requête
 * déclarait jusqu'ici — ce dernier venait du client, et se remplaçait.
 *
 * Le jeton est écrit sous `authToken` par `AuthContext.saveUserToStorage` ;
 * `sessionStorage` prend le relais quand l'utilisateur n'a pas coché « rester
 * connecté ».
 */
function enTetesEcriture(avecCorps = true) {
    let token = null;
    try {
        token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    } catch {
        token = null;
    }

    const headers = {};
    if (avecCorps) headers['Content-Type'] = 'application/json';
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
}

// In-memory cache
let placesCache = {};

const debounce = (fn, delay) => {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), delay);
    };
};

/**
 * Generate a UUID v4
 * @returns {string} UUID
 */
const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

/**
 * Get places from localStorage with timestamp
 * @param {number} peopleId - User's ID
 * @returns {Object} { places: Array, lastModified: string|null }
 */
const getLocalPlacesWithTimestamp = (peopleId) => {
    try {
        const data = localStorage.getItem(STORAGE_KEY);
        const allData = data ? JSON.parse(data) : {};
        const userData = allData[peopleId];

        // Support both old format (array) and new format (object with timestamp)
        if (Array.isArray(userData)) {
            return { places: userData, lastModified: null };
        } else if (userData && typeof userData === 'object') {
            return {
                places: userData.places || [],
                lastModified: userData.lastModified || null
            };
        }
        return { places: [], lastModified: null };
    } catch (error) {
        console.error('Error getting local places:', error);
        return { places: [], lastModified: null };
    }
};

/**
 * Get places from localStorage (fallback) - backward compatible
 * @param {number} peopleId - User's ID
 * @returns {Array}
 */
const getLocalPlaces = (peopleId) => {
    return getLocalPlacesWithTimestamp(peopleId).places;
};

/**
 * Save places to localStorage with timestamp
 * @param {number} peopleId - User's ID
 * @param {Array} places - Places array
 * @param {string} lastModified - ISO timestamp (optional, defaults to now)
 */
const saveLocalPlaces = (peopleId, places, lastModified = null) => {
    try {
        const timestamp = lastModified || new Date().toISOString();

        const data = localStorage.getItem(STORAGE_KEY);
        const allData = data ? JSON.parse(data) : {};
        allData[peopleId] = {
            places: places,
            lastModified: timestamp
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(allData));

        // Verify save
        const verify = localStorage.getItem(STORAGE_KEY);
        const verifyData = JSON.parse(verify);
    } catch (error) {
        console.error('Error saving local places:', error);
    }
};

/**
 * Get local timestamp for a user
 * @param {number} peopleId - User's ID
 * @returns {string|null}
 */
const getLocalTimestamp = (peopleId) => {
    return getLocalPlacesWithTimestamp(peopleId).lastModified;
};

/**
 * Get places count from database (via people_places table)
 * @param {number} userId - User's ID
 * @returns {Promise<number>} Count of places linked to user in DB
 */
const getPlacesCountFromDB = async (userId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/count/${userId}`, {
            method: 'GET',
            credentials: 'include'
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();
        const success = result.success || result.SUCCESS;
        const count = result.count || result.COUNT || 0;

        return success ? count : 0;
    } catch (error) {
        console.error('Error getting places count from DB:', error);
        return -1; // Return -1 to indicate error
    }
};

/**
 * Get current user's peopleUuid and storageKey from localStorage
 * @returns {Object} { peopleuuid, storageKey } or null
 */
const getCurrentUserStorageInfo = () => {
    try {
        const userJson = localStorage.getItem('cabaza_user');
        if (userJson) {
            const user = JSON.parse(userJson);
            return {
                peopleuuid: user.peopleUuid || user.peopleuuid || user.PEOPLEUUID,
                storageKey: user.storageKey || user.STORAGEKEY
            };
        }
    } catch (error) {
        console.error('Error getting user storage info:', error);
    }
    return null;
};

/**
 * Sync places with DB to check for deleted places and update links
 * Called after login to:
 * 1. Check if linked public places still exist (remove placeUuid if deleted)
 * 2. Update place data from DB if changed
 * 3. Link orphan places if a matching public place is found (by googlePlaceId or name+city)
 * @param {number} userId - User's ID
 * @param {Array} places - Places array to sync
 * @returns {Promise<Array>} Synced places with updated flags
 */
const syncPlacesWithDB = async (userId, places, peopleuuid, storageKey) => {
    try {
        const response = await fetch(`${API_BASE_URL}/sync`, {
            method: 'POST',
            headers: enTetesEcriture(),
            credentials: 'include',
            body: JSON.stringify({ userId, places })
        });
        if (!response.ok) return places.map(normalizePlace);
        const result = await response.json();
        const syncedFlags = result.places ?? result.PLACES ?? [];
        const changes = result.changes ?? result.CHANGES ?? 0;

        if (changes > 0 && syncedFlags.length > 0) {
            const flagsMap = new Map(syncedFlags.map(f => [(f.localUuid || f.localuuid || f.LOCALUUID || ''), f]));
            const updated = places.map(p => {
                const key = p.localUuid || p.localuuid || '';
                const flags = flagsMap.get(key);
                if (!flags) return normalizePlace(p);
                return normalizePlace({ ...p, ...flags });
            });
            const timestamp = new Date().toISOString();
            placesCache[userId] = updated;
            saveLocalPlaces(userId, updated, timestamp);
            savePlacesToFile(peopleuuid, storageKey, updated, timestamp).catch(console.error);
            return updated;
        }
        return places.map(normalizePlace);
    } catch (error) {
        console.error('[PLACES SYNC] syncPlacesWithDB error:', error);
        return places.map(normalizePlace);
    }
};

/**
 * Normalize place data - includes ALL form fields
 * @param {Object} place - Raw place data
 * @returns {Object} Normalized place
 */
const normalizePlace = (place) => ({
    // === IDENTIFIERS ===
    // Local UUID (client-side, like contacts)
    localUuid: place.localUuid || place.localuuid || place.LOCALUUID || place.localId || place.localid || place.LOCALID || generateUUID(),
    // Reference to public place UUID in DB (if any)
    placeUuid: place.placeUuid || place.placeuuid || place.PLACEUUID || place.publicPlaceId || place.publicplaceid || place.PUBLICPLACEID || null,
    // Legacy support - keep publicPlaceId for backward compatibility
    publicPlaceId: place.publicPlaceId || place.publicplaceid || place.PUBLICPLACEID || null,

    // === ROLE & OWNERSHIP ===
    // Role: CONTACT (private contact), PUBLIC (linked to DB), PRIVATE (private establishment)
    // Migration: placeType -> role
    role: place.role || place.ROLE || place.placeType || place.placetype || place.PLACETYPE || 'CONTACT',
    // Is user the owner of this establishment?
    isOwner: place.isOwner || place.isowner || place.ISOWNER || false,
    isMember: place.isMember || place.ismember || place.ISMEMBER || false,
    // Relationship type: owner, employee, worker, client, visitor
    relationshipType: place.relationshipType || place.relationshiptype || place.RELATIONSHIPTYPE || '',
    // Legal form
    legalForm: place.legalForm || place.legalform || place.LEGALFORM || '',

    // === BASIC INFO ===
    placeName: place.placeName || place.placename || place.PLACENAME || '',
    // Reference to places_types.code (business category: restaurant, shop, etc.)
    placeTypeCode: place.placeTypeCode || place.placetypecode || place.PLACETYPECODE || place.placeType || place.placetype || place.PLACETYPE || '',
    category: place.category || place.CATEGORY || 'favorite',

    // === GOOGLE DATA ===
    googlePlaceId: place.googlePlaceId || place.googleplaceid || place.GOOGLEPLACEID || '',
    googleUrl: place.googleUrl || place.googleurl || place.GOOGLEURL || '',

    // === ADDRESS COMPONENTS ===
    street: place.street || place.STREET || '',
    number: place.number || place.NUMBER || '',
    box: place.box || place.BOX || '',
    postalCode: place.postalCode || place.postalcode || place.POSTALCODE || '',
    city: place.city || place.CITY || '',
    country: place.country || place.COUNTRY || '',
    countryCode: place.countryCode || place.countrycode || place.COUNTRYCODE || '',

    // === COORDINATES ===
    latitude: place.latitude || place.LATITUDE || null,
    longitude: place.longitude || place.LONGITUDE || null,

    // === CONTACT INFO ===
    phone: place.phone || place.PHONE || '',
    email: place.email || place.EMAIL || '',
    website: place.website || place.WEBSITE || '',
    contactPerson: place.contactPerson || place.contactperson || place.CONTACTPERSON || '',

    // === BUSINESS INFO ===
    tvaNum: place.tvaNum || place.tvanum || place.TVANUM || '',
    bankNum: place.bankNum || place.banknum || place.BANKNUM || '',
    firmType: place.firmType || place.firmtype || place.FIRMTYPE || '',
    description: place.description || place.DESCRIPTION || '',
    contactName: place.contactName || place.contactname || place.CONTACTNAME || place.contactPerson || place.contactperson || place.CONTACTPERSON || '',

    // === SOCIAL MEDIA ===
    facebookUrl: place.facebookUrl || place.facebookurl || place.FACEBOOKURL || '',
    twitterUrl: place.twitterUrl || place.twitterurl || place.TWITTERURL || '',
    youtubeUrl: place.youtubeUrl || place.youtubeurl || place.YOUTUBEURL || '',

    // === MEDIA ===
    imageUrl: place.imageUrl || place.imageurl || place.IMAGEURL || '',

    // === FLAGS ===
    isFavorite: place.isFavorite || place.isfavorite || place.ISFAVORITE || place.addToFavorites || false,
    isActive: place.isActive !== undefined ? place.isActive : (place.isactive !== undefined ? place.isactive : (place.status !== undefined ? place.status : true)),
    parking: place.parking || place.PARKING || false,

    // === TIMESTAMPS ===
    createdAt: place.createdAt || place.createdat || place.CREATEDAT || new Date().toISOString(),
    updatedAt: place.updatedAt || place.updatedat || place.UPDATEDAT || new Date().toISOString(),

    // === CREATOR ===
    createdByPeopleId: place.createdByPeopleId || place.createdbypeopleid || place.CREATEDBYPEOPLEID || null,
    createdByName: place.createdByName || place.createdbyname || place.CREATEDBYNAME || ''
});

/**
 * Backup places file on server before saving
 * @param {string} peopleuuid - Owner's peopleUuid
 * @returns {Promise<Object>} Result with success status
 */
export const backupPlacesFile = async (peopleuuid) => {
    try {
        const response = await fetch(`${API_BASE_URL}/backup-file`, {
            method: 'POST',
            credentials: 'include',
            headers: enTetesEcriture(),
            body: JSON.stringify({ peopleuuid })
        });

        if (!response.ok) {
            console.warn('Backup places file warning:', response.status);
            return { success: false, message: `HTTP ${response.status}` };
        }

        const result = await response.json();
        return result;
    } catch (error) {
        console.warn('Error backing up places file:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Save places to encrypted file on server with timestamp
 * Creates a backup of the existing file before saving
 * @param {string} peopleuuid - Owner's peopleUuid
 * @param {string} storageKey - Encryption key
 * @param {Array} places - Places to save
 * @param {string} lastModified - ISO timestamp
 * @returns {Promise<Object>} Result with success status
 */
export const savePlacesToFile = async (peopleuuid, storageKey, places, lastModified = null) => {
    try {
        // Create backup before saving (don't block on failure)
        backupPlacesFile(peopleuuid).catch(err => console.warn('Backup failed:', err));

        const timestamp = lastModified || new Date().toISOString();
        const response = await fetch(`${API_BASE_URL}/save-file`, {
            method: 'POST',
            credentials: 'include',
            headers: enTetesEcriture(),
            body: JSON.stringify({
                peopleuuid,
                storageKey,
                places,
                lastModified: timestamp
            })
        });

        if (!response.ok) {
            const text = await response.text();
            console.error('Save places file error response:', text);
            return { success: false, message: `HTTP ${response.status}` };
        }

        const result = await response.json();
        return result;
    } catch (error) {
        console.error('Error saving places to file:', error);
        return { success: false, message: error.message };
    }
};

const savePlacesToFileDebounced = debounce(savePlacesToFile, 2000);

/**
 * Load places from encrypted file on server with timestamp
 * @param {string} peopleuuid - Owner's peopleUuid
 * @param {string} storageKey - Decryption key
 * @returns {Promise<Object>} { places: Array, lastModified: string|null }
 */
export const loadPlacesFromFileWithTimestamp = async (peopleuuid, storageKey) => {
    try {

        const response = await fetch(`${API_BASE_URL}/load-file`, {
            method: 'POST',
            credentials: 'include',
            headers: enTetesEcriture(),
            body: JSON.stringify({
                peopleuuid,
                storageKey
            })
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Load places file error response:', errorText);
            return { places: [], lastModified: null };
        }

        const text = await response.text();
        let result;
        try {
            result = JSON.parse(text);
        } catch (parseErr) {
            console.error('JSON parse error:', parseErr);
            return { places: [], lastModified: null };
        }

        const success = result.success || result.SUCCESS;
        const places = result.places || result.PLACES || [];
        const lastModified = result.lastModified || result.LASTMODIFIED || null;

        return success ? { places, lastModified } : { places: [], lastModified: null };
    } catch (error) {
        console.error('Error loading places from file:', error);
        return { places: [], lastModified: null };
    }
};

/**
 * Load places from encrypted file on server (backward compatible)
 * @param {string} peopleuuid - Owner's peopleUuid
 * @param {string} storageKey - Decryption key
 * @returns {Promise<Array>} Places array
 */
export const loadPlacesFromFile = async (peopleuuid, storageKey) => {
    const result = await loadPlacesFromFileWithTimestamp(peopleuuid, storageKey);
    return result.places;
};

/**
 * Sync places FROM server on login
 *
 * NEW LOGIC: people_places is the MASTER table (source of truth)
 *
 * Flow:
 * 1. Send local places (localStorage) to API
 * 2. API adds missing CONTACT places to people_places
 * 3. API returns ALL places from people_places (master)
 * 4. Update localStorage and fileserver (caches) with master data
 *
 * @param {number} userId - User's ID
 * @param {string} peopleuuid - User's peopleUuid
 * @param {string} storageKey - User's storageKey for decryption
 * @returns {Promise<Array>} Loaded places
 */
export const syncPlacesFromServer = async (userId, peopleuuid, storageKey) => {
    if (!userId || !peopleuuid || !storageKey) {
        console.warn('[PLACES SYNC] Missing params');
        return [];
    }

    try {
        const [dbCount, localData, serverData] = await Promise.all([
            getPlacesCountFromDB(userId),
            Promise.resolve(getLocalPlacesWithTimestamp(userId)),
            loadPlacesFromFileWithTimestamp(peopleuuid, storageKey)
        ]);

        const localTimestamp = localData.lastModified ? new Date(localData.lastModified) : null;
        const serverTimestamp = serverData.lastModified ? new Date(serverData.lastModified) : null;

        const localMatchesDB = localData.places.length === dbCount;
        const serverMatchesDB = serverData.places.length === dbCount;

        let placesToSync = null;

        if (localMatchesDB && serverMatchesDB) {
            if (localTimestamp && serverTimestamp) {
                if (localTimestamp >= serverTimestamp) {
                    placesCache[userId] = localData.places.map(normalizePlace);
                    if (localTimestamp > serverTimestamp) {
                        await savePlacesToFile(peopleuuid, storageKey, localData.places, localData.lastModified);
                    }
                    placesToSync = localData.places;
                } else {
                    placesCache[userId] = serverData.places.map(normalizePlace);
                    saveLocalPlaces(userId, serverData.places, serverData.lastModified);
                    placesToSync = serverData.places;
                }
            } else {
                placesCache[userId] = localData.places.map(normalizePlace);
                const timestamp = new Date().toISOString();
                saveLocalPlaces(userId, localData.places, timestamp);
                await savePlacesToFile(peopleuuid, storageKey, localData.places, timestamp);
                placesToSync = localData.places;
            }
        } else if (localMatchesDB) {
            placesCache[userId] = localData.places.map(normalizePlace);
            const timestamp = localData.lastModified || new Date().toISOString();
            await savePlacesToFile(peopleuuid, storageKey, localData.places, timestamp);
            placesToSync = localData.places;
        } else if (serverMatchesDB) {
            placesCache[userId] = serverData.places.map(normalizePlace);
            saveLocalPlaces(userId, serverData.places, serverData.lastModified || new Date().toISOString());
            placesToSync = serverData.places;
        } else if (dbCount === 0) {
            if (serverData.places.length > 0) {
                placesCache[userId] = serverData.places.map(normalizePlace);
                saveLocalPlaces(userId, serverData.places, serverData.lastModified || new Date().toISOString());
                return await syncPlacesWithDB(userId, serverData.places, peopleuuid, storageKey);
            }
            placesCache[userId] = [];
            saveLocalPlaces(userId, [], new Date().toISOString());
            return [];
        } else {
            try {
                const response = await fetch(`${API_BASE_URL}/people/${userId}`, {
                    method: 'GET',
                    credentials: 'include'
                });
                if (response.ok) {
                    const result = await response.json();
                    const apiPlaces = (result.data || result.DATA || []).map(normalizePlace);
                    const serverContacts = (serverData.places || [])
                        .filter(p => (p.role || p.ROLE || '').toUpperCase() === 'CONTACT')
                        .map(normalizePlace);
                    const merged = [...apiPlaces, ...serverContacts];
                    const timestamp = new Date().toISOString();
                    placesCache[userId] = merged;
                    saveLocalPlaces(userId, merged, timestamp);
                    await savePlacesToFile(peopleuuid, storageKey, merged, timestamp);
                    placesToSync = merged;
                }
            } catch (apiErr) {
                console.error('[PLACES SYNC] API reload failed:', apiErr);
            }
        }

        if (placesToSync && placesToSync.length > 0) {
            return await syncPlacesWithDB(userId, placesToSync, peopleuuid, storageKey);
        } else if (placesToSync !== null) {
            return placesToSync || [];
        }

        const fallback = localTimestamp && serverTimestamp
            ? (localTimestamp >= serverTimestamp ? localData.places : serverData.places)
            : (localData.places.length > 0 ? localData.places : serverData.places);
        return fallback.map(normalizePlace);

    } catch (error) {
        console.error('[PLACES SYNC] Fatal error:', error);
        return getLocalPlaces(userId).map(normalizePlace);
    }
};

/**
 * Get all user places (private + linked to public)
 * @param {number} peopleId - User's ID
 * @param {boolean} forceRefresh - Force refresh (skip cache)
 * @returns {Promise<Array>} Array of place objects
 */
export const getUserPlaces = async (peopleId, forceRefresh = false) => {
    // Return from cache if available and not forcing refresh
    if (!forceRefresh && placesCache[peopleId]) {
        return placesCache[peopleId];
    }

    try {
        // Get local places
        let localPlaces = getLocalPlaces(peopleId);

        // If localStorage is empty, try to load from server file first
        if (localPlaces.length === 0) {
            const storageInfo = getCurrentUserStorageInfo();
            if (storageInfo?.peopleuuid && storageInfo?.storageKey) {
                const serverPlaces = await loadPlacesFromFile(storageInfo.peopleuuid, storageInfo.storageKey);
                if (serverPlaces && serverPlaces.length > 0) {
                    localPlaces = serverPlaces;
                    saveLocalPlaces(peopleId, serverPlaces);
                }
            }
        }

        // Normalize places
        const places = localPlaces.map(normalizePlace);

        // Update cache
        placesCache[peopleId] = places;

        return places;
    } catch (error) {
        console.error('Error getting user places:', error);
        return getLocalPlaces(peopleId).map(normalizePlace);
    }
};

/**
 * Get visible places (excluding PRIVATE type if needed)
 * @param {number} peopleId - User's ID
 * @param {boolean} forceRefresh - Force refresh
 * @returns {Promise<Array>} Array of visible place objects
 */
export const getVisibleUserPlaces = async (peopleId, forceRefresh = false) => {
    const allPlaces = await getUserPlaces(peopleId, forceRefresh);
    // For now, return all places (CONTACT and PUBLIC are both visible to owner)
    return allPlaces;
};

/**
 * Add a new place for user (private by default)
 * @param {number} userId - User's ID
 * @param {Object} placeData - Place data to add
 * @returns {Promise<Object>} Created place or null
 */
export const addUserPlace = async (userId, placeData) => {
    try {
        const localUuid = placeData.localUuid || placeData.localuuid || generateUUID();
        const placeUuid = placeData.placeUuid || placeData.placeuuid || null;

        const response = await fetch(`${API_BASE_URL}/link`, {
            method: 'POST',
            headers: enTetesEcriture(),
            credentials: 'include',
            body: JSON.stringify({
                localUuid,
                placeUuid: placeUuid || '',
                peopleId: userId,
                role: placeData.role || (placeUuid ? 'PUBLIC' : 'CONTACT'),
                isFavorite: placeData.isFavorite ? 1 : 0,
                isOwner: placeData.isOwner ? 1 : 0,
                isMember: placeData.isMember ? 1 : 0
            })
        });

        let dbData = {};
        if (response.ok) {
            const result = await response.json();
            dbData = result.data || result.DATA || {};
        }

        const newPlace = normalizePlace({
            ...placeData,
            ...dbData,
            localUuid,
            placeUuid: placeUuid || dbData.placeUuid || dbData.PLACEUUID || null,
            role: placeData.role || (placeUuid ? 'PUBLIC' : 'CONTACT'),
            isFavorite: placeData.isFavorite || false,
            isOwner: placeData.isOwner || false,
            isMember: placeData.isMember || false,
            createdAt: new Date().toISOString()
        });

        if (!placesCache[userId]) placesCache[userId] = [];
        placesCache[userId] = [...placesCache[userId].filter(p => p.localUuid !== localUuid), newPlace];

        saveLocalPlaces(userId, placesCache[userId]);

        const storageInfo = getCurrentUserStorageInfo();
        if (storageInfo?.peopleuuid && storageInfo?.storageKey) {
            savePlacesToFileDebounced(storageInfo.peopleuuid, storageInfo.storageKey, placesCache[userId], new Date().toISOString());
        }

        return newPlace;
    } catch (error) {
        console.error('Error adding user place:', error);
        throw error;
    }
};

/**
 * Add a place from Google Places data
 * @param {number} userId - User's ID
 * @param {Object} googlePlaceData - Data from Google Places API
 * @returns {Promise<Object>} Created place
 */
export const addPlaceFromGoogle = async (userId, googlePlaceData) => {
    return addUserPlace(userId, {
        placeName: googlePlaceData.placeName || googlePlaceData.name,
        googlePlaceId: googlePlaceData.googlePlaceId || googlePlaceData.place_id || '',
        street: googlePlaceData.street || '',
        number: googlePlaceData.number || '',
        box: googlePlaceData.box || '',
        postalCode: googlePlaceData.postalCode || '',
        city: googlePlaceData.city || '',
        country: googlePlaceData.country || '',
        countryCode: googlePlaceData.countryCode || '',
        latitude: googlePlaceData.latitude || googlePlaceData.lat,
        longitude: googlePlaceData.longitude || googlePlaceData.lng,
        category: 'favorite',
        isOwner: false // User is not owner by default
    });
};

/**
 * Update a place
 * @param {number} userId - User's ID
 * @param {string} localUuid - Place localUuid
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated place or null
 */
export const updateUserPlace = async (userId, localUuid, updates) => {
    try {
        if (!placesCache[userId]) {
            placesCache[userId] = getLocalPlaces(userId).map(normalizePlace);
        }

        // Support both localUuid and legacy localId
        const placeIndex = placesCache[userId].findIndex(p => p.localUuid === localUuid || p.localId === localUuid);
        if (placeIndex === -1) {
            console.error('Place not found:', localUuid);
            return null;
        }

        // Update place
        placesCache[userId][placeIndex] = {
            ...placesCache[userId][placeIndex],
            ...updates,
            updatedAt: new Date().toISOString()
        };

        saveLocalPlaces(userId, placesCache[userId]);

        const storageInfo = getCurrentUserStorageInfo();
        if (storageInfo?.peopleuuid && storageInfo?.storageKey) {
            savePlacesToFileDebounced(storageInfo.peopleuuid, storageInfo.storageKey, placesCache[userId], new Date().toISOString());
        }

        return placesCache[userId][placeIndex];
    } catch (error) {
        console.error('Error updating place:', error);
        return null;
    }
};

/**
 * Remove a place
 * @param {number} userId - User's ID
 * @param {string} localUuid - Place localUuid to remove
 * @returns {Promise<boolean>} Success status
 */
export const removeUserPlace = async (userId, localUuid) => {
    try {
        if (!placesCache[userId]) {
            placesCache[userId] = getLocalPlaces(userId).map(normalizePlace);
        }

        const initialLength = placesCache[userId].length;
        // Support both localUuid and legacy localId
        placesCache[userId] = placesCache[userId].filter(p => p.localUuid !== localUuid && p.localId !== localUuid);

        if (placesCache[userId].length === initialLength) {
            console.error('Place not found:', localUuid);
            return false;
        }

        saveLocalPlaces(userId, placesCache[userId]);

        const storageInfo = getCurrentUserStorageInfo();
        if (storageInfo?.peopleuuid && storageInfo?.storageKey) {
            savePlacesToFileDebounced(storageInfo.peopleuuid, storageInfo.storageKey, placesCache[userId], new Date().toISOString());
        }
        return true;
    } catch (error) {
        console.error('Error removing place:', error);
        return false;
    }
};

/**
 * Make a private place public (save to DB)
 * User must claim ownership first
 * @param {number} userId - User's ID
 * @param {string} localUuid - Place localUuid
 * @returns {Promise<Object>} Result with success status and public place data
 */
export const makePublicPlace = async (userId, localUuid) => {
    try {
        if (!placesCache[userId]) {
            placesCache[userId] = getLocalPlaces(userId).map(normalizePlace);
        }

        // Support both localUuid and legacy localId
        const place = placesCache[userId].find(p => p.localUuid === localUuid || p.localId === localUuid);
        if (!place) {
            return { success: false, message: 'Place not found' };
        }

        if (!place.isOwner) {
            return { success: false, message: 'You must be the owner to make this place public' };
        }

        if (place.placeUuid || place.publicPlaceId) {
            return { success: false, message: 'Place is already public' };
        }

        // Create public place in DB
        const publicPlaceData = {
            placeName: place.placeName,
            googlePlaceId: place.googlePlaceId,
            street: place.street,
            number: place.number,
            box: place.box,
            postalCode: place.postalCode,
            city: place.city,
            country: place.country,
            countryCode: place.countryCode,
            latitude: place.latitude,
            longitude: place.longitude,
            phone: place.phone,
            email: place.email,
            website: place.website,
            description: place.description,
            placeTypeCode: place.placeTypeCode || '',
            tvaNum: place.tvaNum,
            createdById: userId
        };

        const result = await createPublicPlace(publicPlaceData);

        if (result.success && result.data) {
            // Update local place with public UUID
            const publicPlaceId = result.data.id || result.data.ID;
            const placeUuid = result.data.placeUuid || result.data.PLACEUUID || null;
            await updateUserPlace(userId, localUuid, {
                publicPlaceId: publicPlaceId,
                placeUuid: placeUuid,
                role: 'PUBLIC'
            });

            return {
                success: true,
                message: 'Place is now public',
                data: result.data,
                publicPlaceId: publicPlaceId,
                placeUuid: placeUuid
            };
        }

        return { success: false, message: result.message || 'Failed to create public place' };
    } catch (error) {
        console.error('Error making place public:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Claim ownership of a place
 * @param {number} userId - User's ID
 * @param {string} localUuid - Place localUuid
 * @param {boolean} isOwner - Whether user is owner
 * @returns {Promise<Object>} Updated place
 */
export const setPlaceOwnership = async (userId, localUuid, isOwner) => {
    return updateUserPlace(userId, localUuid, { isOwner });
};

/**
 * Search user's private places
 * @param {number} userId - User's ID
 * @param {string} keyword - Search term
 * @param {number} limit - Max results
 * @returns {Promise<Array>} Matching places
 */
export const searchUserPlaces = async (userId, keyword, limit = 10) => {
    if (!keyword || keyword.length < 2) {
        return [];
    }

    const places = await getUserPlaces(userId);
    const lowerKeyword = keyword.toLowerCase();

    return places
        .filter(p =>
            p.placeName?.toLowerCase().includes(lowerKeyword) ||
            p.street?.toLowerCase().includes(lowerKeyword) ||
            p.city?.toLowerCase().includes(lowerKeyword)
        )
        .slice(0, limit);
};

/**
 * Get place by localUuid (or legacy localId)
 * @param {number} userId - User's ID
 * @param {string} localUuid - Place localUuid
 * @returns {Object|null} Place or null
 */
export const getPlaceByLocalUuid = (userId, localUuid) => {
    const places = placesCache[userId] || getLocalPlaces(userId);
    return places.find(p => p.localUuid === localUuid || p.localId === localUuid) || null;
};

// Legacy alias
export const getPlaceByLocalId = getPlaceByLocalUuid;

/**
 * Get place by placeUuid (or legacy publicPlaceId)
 * @param {number} userId - User's ID
 * @param {string} placeUuid - Public place UUID from DB
 * @returns {Object|null} Place or null
 */
export const getPlaceByPlaceUuid = (userId, placeUuid) => {
    const places = placesCache[userId] || getLocalPlaces(userId);
    return places.find(p => p.placeUuid === placeUuid || p.publicPlaceId === placeUuid) || null;
};

// Legacy alias
export const getPlaceByPublicId = getPlaceByPlaceUuid;

/**
 * Get count of user's places
 * @param {number} peopleId - User's ID
 * @returns {number} Number of places
 */
export const getUserPlacesCount = (peopleId) => {
    const places = placesCache[peopleId] || getLocalPlaces(peopleId);
    return places.length;
};

/**
 * Toggle isFavorite on a place (localStorage + file server)
 * @param {number} userId - User's ID
 * @param {string} localUuid - Place localUuid
 * @returns {Promise<boolean>} New isFavorite value, or null on error
 */
export const togglePlaceFavorite = async (userId, localUuid) => {
    try {
        if (!placesCache[userId]) {
            placesCache[userId] = getLocalPlaces(userId).map(normalizePlace);
        }
        const place = placesCache[userId].find(p => p.localUuid === localUuid || p.localId === localUuid);
        if (!place) return null;

        const newFavorite = !place.isFavorite;
        await updateUserPlace(userId, localUuid, { isFavorite: newFavorite });
        return newFavorite;
    } catch (error) {
        console.error('Error toggling place favorite:', error);
        return null;
    }
};

/**
 * Clear cache (useful on logout)
 * @param {number} peopleId - User's ID (optional)
 */
export const clearPlacesCache = (peopleId = null) => {
    if (peopleId) {
        delete placesCache[peopleId];
    } else {
        placesCache = {};
    }
};

/**
 * Preload places for a user
 * @param {number} peopleId - User's ID
 */
export const preloadPlaces = async (peopleId) => {
    await getUserPlaces(peopleId);
};

export default {
    getUserPlaces,
    getVisibleUserPlaces,
    addUserPlace,
    addPlaceFromGoogle,
    updateUserPlace,
    removeUserPlace,
    makePublicPlace,
    setPlaceOwnership,
    searchUserPlaces,
    getPlaceByLocalUuid,
    getPlaceByLocalId, // Legacy alias
    getPlaceByPlaceUuid,
    getPlaceByPublicId, // Legacy alias
    getUserPlacesCount,
    clearPlacesCache,
    preloadPlaces,
    syncPlacesFromServer,
    savePlacesToFile,
    loadPlacesFromFile
};
